import React, { useEffect, useMemo, useRef } from "react";

// JotForm public form URL: https://form.jotform.com/<id> or https://form.jotform.us/<id>
export const JOTFORM_URL_RE = /^https?:\/\/(?:form|submit)\.jotform\.(?:com|us|me|co)\/(\d+)/i;

export function getJotFormId(url) {
  const m = url?.match(JOTFORM_URL_RE);
  return m ? m[1] : null;
}

// Hosts JotForm serves forms from. A resize message is only honoured if it came
// from one of these — otherwise any page in any iframe could resize ours.
// Mirrors the list inside JotForm's own for-form-embed-handler.js.
const JOTFORM_HOSTS = [
  "jotform.com",
  "jotform.us",
  "jotform.ca",
  "jotformeu.com",
  "jotformpro.com",
  "jotform.me",
  "myjotform.com",
  "jotformz.com",
  "jotform.co",
  "jotform.net",
  "jotform2.com",
  "jotfor.ms",
  "jot-form.com",
  "jotformform.com",
  "jotformforms.com",
  "jotform.pro",
  "jotform.com.tr",
  "jotform.dev",
  "jform.co.kr",
  "formresponse.com",
  "jotformsign.com",
  "jotform.ooo",
  "jotform.ai",
  "jotform.io",
];

function isJotFormOrigin(origin) {
  try {
    const host = new URL(origin).hostname.toLowerCase().replace(/\.$/, "");
    return JOTFORM_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
  } catch {
    return false;
  }
}

/**
 * Builds the src the form is actually loaded with.
 *
 * `isIframeEmbed=1` is not decoration: without it the form never posts
 * setHeight messages, so the iframe would sit at its initial height forever
 * and tall forms would be clipped. JotForm's own handler injects this by
 * cloning and replacing the iframe node after mount; building the URL up front
 * is equivalent and avoids a second load.
 *
 * Parent query params are forwarded so ?field=value prefill links keep working,
 * matching the handler's behaviour. Params already on the form URL win.
 */
function buildEmbedSrc(url) {
  try {
    const u = new URL(url, window.location.href);
    new URLSearchParams(window.location.search).forEach((value, key) => {
      if (!u.searchParams.has(key)) u.searchParams.set(key, value);
    });
    u.searchParams.set("isIframeEmbed", "1");
    u.searchParams.set("parentURL", window.location.href);
    return u.toString();
  } catch {
    return url;
  }
}

/**
 * Auto-resizing JotForm embed. The iframe grows to its content height, so the
 * form scrolls with the page rather than inside a fixed-height box (it is
 * scrolling="no" — if the height is wrong the overflow is simply unreachable).
 *
 * WHY THIS DOES NOT USE window.jotformEmbedHandler
 * ------------------------------------------------
 * JotForm's for-form-embed-handler.js keeps ONE global message bus, and the
 * first thing every call does is destroy the previous one:
 *
 *     window.jotformEmbedHandler = function (selector, origin) {
 *       window.__jotformEmbedHandlerBus?.destroy?.()   // <-- kills the last form
 *       ...
 *       window.__jotformEmbedHandlerBus = bus
 *     }
 *
 * destroy() removes the "message" listener, and the bus closes over a single
 * selector. So it supports exactly one form per page: with two embeds the
 * second call silently unhooks the first, leaving that iframe stuck at its
 * initial height with its content unreachable. That is what happened when the
 * Biz Connect page grew a second (exhibitor) form and the attendee form above
 * it stopped resizing.
 *
 * Listening per-iframe instead — discriminating on
 * `event.source === iframe.contentWindow` — is correct for any number of forms
 * on a page, and drops a third-party script we no longer need.
 *
 * If a form ever needs the handler's `loadScript` action (used by some payment
 * and widget integrations, which inject a script into the parent page), that is
 * the reason to revisit this.
 *
 * `scrollToTopOnLoad` defaults to true to preserve the original behaviour on
 * resource pages, where the form is the point of the page. Pass false on long
 * marketing pages — otherwise the lazily-loaded iframe yanks the reader back to
 * the top of the page when it finishes loading. It doubles as the answer to
 * "may this component move the scroll position?", so it also gates the form's
 * own scrollIntoView requests.
 */
export default function JotFormEmbed({
  url,
  formId,
  title,
  scrollToTopOnLoad = true,
  minHeight = 600,
}) {
  const iframeRef = useRef(null);
  const resolvedId = formId || getJotFormId(url);
  const iframeId = `JotFormIFrame-${resolvedId}`;
  const src = useMemo(() => buildEmbedSrc(url), [url]);

  useEffect(() => {
    const onMessage = (event) => {
      const iframe = iframeRef.current;
      if (!iframe || typeof event.data !== "string") return;
      // The check that makes multiple forms on one page work: act only on
      // messages from this iframe's own window.
      if (!iframe.contentWindow || event.source !== iframe.contentWindow) return;
      if (!isJotFormOrigin(event.origin)) return;

      const [action, rawValue] = event.data.split(":");
      const value = Number.parseInt(rawValue, 10);

      switch (action) {
        case "setHeight":
          if (!Number.isNaN(value)) {
            iframe.style.height = `${value}px`;
            // A minHeight left over from the initial render would otherwise
            // hold the frame taller than its content.
            if (Number.parseInt(iframe.style.minHeight, 10) > value) {
              iframe.style.minHeight = `${value}px`;
            }
          }
          break;
        case "setMinHeight":
          if (!Number.isNaN(value)) iframe.style.minHeight = `${value}px`;
          break;
        case "collapseErrorPage":
          // JotForm's error page can be far taller than the viewport.
          if (iframe.clientHeight > window.innerHeight) {
            iframe.style.height = `${window.innerHeight}px`;
          }
          break;
        case "scrollIntoView":
          if (scrollToTopOnLoad) iframe.scrollIntoView();
          break;
        default:
          break;
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [scrollToTopOnLoad]);

  return (
    <iframe
      ref={iframeRef}
      id={iframeId}
      title={title || "Form"}
      src={src}
      allow="geolocation; microphone; camera; fullscreen"
      allowtransparency="true"
      frameBorder="0"
      scrolling="no"
      loading="lazy"
      onLoad={scrollToTopOnLoad ? () => window.parent.scrollTo(0, 0) : undefined}
      style={{
        minWidth: "100%",
        maxWidth: "100%",
        // width:1px is the iOS Safari fix for iframes shrinking below 100%
        width: "1px",
        // Initial height — replaced by the content height on the first
        // setHeight message.
        height: `${minHeight}px`,
        border: "none",
        display: "block",
      }}
    />
  );
}
